import { useState, useEffect, useMemo } from 'react';

// Search text and selected user are owned by the page (persisted filters, see
// src/_domains/leads/helpers/filters.js); this hook only applies them to the
// loaded columns. A restored search applies right away, typing is debounced.
export const useLeadsFiltering = (columns, hideLeads, searchQuery, selectedUserId) => {
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery || "");

  // Debounce search query to improve performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery || "");
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter leads based on search query and selected user
  const { filteredColumns, filteredHideLeads } = useMemo(() => {
    let filtered = columns;
    let filteredHideLeadsData = hideLeads;

    // Apply search filter
    if (debouncedSearchQuery && debouncedSearchQuery.trim() !== '') {
      const query = debouncedSearchQuery.toLowerCase().trim();
      
      // Filter function for search
      const filterBySearch = (lead) => {
        const searchFields = [
          lead.name,
          lead.email,
          lead.phone,
          lead.company_name,
          lead.source,
          lead.company_type,
          lead.identification_number,
        ];

        return searchFields.some(field => 
          field && field.toString().toLowerCase().includes(query)
        );
      };

      filtered = filtered.map(column => ({
        ...column,
        leads: (column.leads || []).filter(filterBySearch)
      }));

      // Filter hideLeads.form as well
      if (hideLeads && hideLeads.form && hideLeads.form.length > 0) {
        const filteredFormLeads = hideLeads.form.filter(filterBySearch);
        filteredHideLeadsData = {
          ...hideLeads,
          form: filteredFormLeads
        };
      }
    }

    // Apply user filter
    if (selectedUserId !== null && selectedUserId !== undefined) {
      const filterByUser = (lead) => {
        // If selectedUserId is null, show all leads (handled above)
        // If selectedUserId is set, only show leads assigned to that user
        return lead.assigned_to === selectedUserId;
      };

      filtered = filtered.map(column => ({
        ...column,
        leads: (column.leads || []).filter(filterByUser)
      }));

      // Filter hideLeads.form as well
      if (filteredHideLeadsData && filteredHideLeadsData.form && filteredHideLeadsData.form.length > 0) {
        const filteredFormLeads = filteredHideLeadsData.form.filter(filterByUser);
        filteredHideLeadsData = {
          ...filteredHideLeadsData,
          form: filteredFormLeads
        };
      }
    }

    return { filteredColumns: filtered, filteredHideLeads: filteredHideLeadsData };
  }, [columns, hideLeads, debouncedSearchQuery, selectedUserId]);

  // Calculate search results count
  const searchResultsCount = useMemo(() => {
    if ((!debouncedSearchQuery || debouncedSearchQuery.trim() === '') && (selectedUserId === null || selectedUserId === undefined)) {
      return null;
    }
    return filteredColumns.reduce((total, column) => total + (column.leads || []).length, 0);
  }, [filteredColumns, debouncedSearchQuery, selectedUserId]);

  return {
    debouncedSearchQuery,
    filteredColumns,
    filteredHideLeads,
    searchResultsCount,
  };
};